/**
 * ClientDetailScreen — tabbed drill-down for /clients/:id.
 *
 * Tabs:
 *   - overview (counts + last upload)
 *   - files (per-client uploads across runs)
 *   - runs (campaigns the client is invited to)
 *   - notes (free-form timestamped notes)
 */

import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ApiError, api, type CampaignResponse } from "../lib/api";

interface Activity {
  total_files: number;
  total_bytes: number;
  last_file_uploaded_at: string | null;
  runs_count: number;
  notes_count: number;
}

export function ClientDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activity, setActivity] = useState<Activity | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    if (!id) return;
    try {
      setActivity(await api.clientActivity(id));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError("לקוח לא נמצא.");
      } else {
        setError("טעינת פרטי הלקוח נכשלה.");
      }
    }
  };

  useEffect(() => {
    reload();
  }, [id]);

  if (error)
    return (
      <Stack>
        <Text c="red">{error}</Text>
        <Button variant="subtle" onClick={() => navigate("/clients")}>
          חזרה לרשימת לקוחות
        </Button>
      </Stack>
    );
  if (!id) return <Text c="red">חסר client_id.</Text>;
  if (activity === null)
    return (
      <Group py="lg">
        <Loader size="sm" />
      </Group>
    );

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>פרטי לקוח</Title>
        <Button variant="subtle" onClick={() => navigate("/clients")}>
          חזרה לרשימה
        </Button>
      </Group>

      <Card withBorder padding="md">
        <Group gap="xl">
          <Stat label="קבצים" value={activity.total_files} />
          <Stat label="קמפיינים" value={activity.runs_count} />
          <Stat label="הערות" value={activity.notes_count} />
          <Stat
            label="סה״כ נפח"
            value={`${(activity.total_bytes / 1024 / 1024).toFixed(1)} MB`}
          />
          <Stat
            label="העלאה אחרונה"
            value={
              activity.last_file_uploaded_at
                ? new Date(activity.last_file_uploaded_at).toLocaleString("he-IL")
                : "—"
            }
          />
        </Group>
      </Card>

      <PauseTogglePanel clientId={id} />


      <Tabs defaultValue="files">
        <Tabs.List>
          <Tabs.Tab value="files">קבצים</Tabs.Tab>
          <Tabs.Tab value="runs">קמפיינים</Tabs.Tab>
          <Tabs.Tab value="notes">הערות</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="files" pt="md">
          <FilesTab clientId={id} />
        </Tabs.Panel>
        <Tabs.Panel value="runs" pt="md">
          <RunsTab clientId={id} />
        </Tabs.Panel>
        <Tabs.Panel value="notes" pt="md">
          <NotesTab clientId={id} onChanged={reload} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text fw={600}>{value}</Text>
    </div>
  );
}

const STATUS_COLOR: Record<string, string> = {
  uploaded: "blue",
  pulled: "yellow",
  acked: "green",
  archived: "gray",
};

function FilesTab({ clientId }: { clientId: string }) {
  const [files, setFiles] = useState<Awaited<ReturnType<typeof api.clientFiles>>["files"] | null>(null);
  useEffect(() => {
    api.clientFiles(clientId).then((r) => setFiles(r.files));
  }, [clientId]);
  if (files === null) return <Loader size="sm" />;
  if (files.length === 0) return <Text c="dimmed">אין קבצים עדיין.</Text>;
  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>מסמך</Table.Th>
          <Table.Th>גודל</Table.Th>
          <Table.Th>סטטוס</Table.Th>
          <Table.Th>הועלה</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {files.map((f) => (
          <Table.Tr key={f.file_id}>
            <Table.Td>{f.document_label ?? "—"}</Table.Td>
            <Table.Td>{(f.size_bytes / 1024).toFixed(1)} KB</Table.Td>
            <Table.Td>
              <Badge color={STATUS_COLOR[f.status] ?? "gray"} variant="light">
                {f.status}
              </Badge>
            </Table.Td>
            <Table.Td>{new Date(f.uploaded_at).toLocaleString("he-IL")}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function RunsTab({ clientId }: { clientId: string }) {
  const [runs, setRuns] = useState<Awaited<ReturnType<typeof api.clientRuns>>["runs"] | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignResponse[]>([]);
  useEffect(() => {
    Promise.all([
      api.clientRuns(clientId),
      api.campaignsList().catch(() => ({ campaigns: [] as CampaignResponse[] })),
    ]).then(([r, c]) => {
      setRuns(r.runs);
      setCampaigns(c.campaigns);
    });
  }, [clientId]);
  if (runs === null) return <Loader size="sm" />;
  if (runs.length === 0) return <Text c="dimmed">לקוח זה לא הוזמן עדיין לקמפיין.</Text>;
  const campaignNameById = Object.fromEntries(campaigns.map((c) => [c.campaign_id, c.name]));
  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>קמפיין</Table.Th>
          <Table.Th>דדליין</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {runs.map((r) => (
          <Table.Tr key={r.run_id}>
            <Table.Td>{campaignNameById[r.campaign_id] ?? r.period_label}</Table.Td>
            <Table.Td>{new Date(r.deadline_at).toLocaleString("he-IL")}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function NotesTab({ clientId, onChanged }: { clientId: string; onChanged: () => void }) {
  const [notes, setNotes] = useState<Awaited<ReturnType<typeof api.clientNotes>>["notes"] | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setNotes((await api.clientNotes(clientId)).notes);
  };

  useEffect(() => {
    reload();
  }, [clientId]);

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.clientNoteCreate(clientId, body);
      setDraft("");
      await reload();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      <Card withBorder padding="md">
        <Stack>
          <Textarea
            label="הוסף הערה"
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            placeholder="לדוגמה: שיחה עם הלקוח 7/5/2026 — מבקש דחייה..."
            minRows={3}
            dir="rtl"
          />
          <Group justify="flex-end">
            <Button onClick={submit} loading={busy} disabled={!draft.trim()}>
              שמור הערה
            </Button>
          </Group>
        </Stack>
      </Card>
      {notes === null ? (
        <Loader size="sm" />
      ) : notes.length === 0 ? (
        <Text c="dimmed">אין הערות עדיין.</Text>
      ) : (
        <Stack gap="xs">
          {notes.map((n) => (
            <Card key={n.note_id} withBorder padding="sm">
              <Text size="xs" c="dimmed">
                {new Date(n.created_at).toLocaleString("he-IL")}
              </Text>
              <Text dir="rtl" style={{ whiteSpace: "pre-wrap" }}>
                {n.body}
              </Text>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * PauseTogglePanel — flips clients.reminders_paused via PATCH. The
 * scheduler honors this across all of the client's active runs.
 *
 * The toggle reads the current state lazily (we don't pass it in from
 * the parent because the activity endpoint doesn't include it; rather
 * than thread it through, fetch from /v1/clients/:id once).
 */
function PauseTogglePanel({ clientId }: { clientId: string }) {
  const [paused, setPaused] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initial fetch — read the current pause flag. We use the regular
  // /v1/clients list and find this id in it. /v1/clients/:id exists too
  // but doesn't return reminders_paused yet.
  useEffect(() => {
    let cancelled = false;
    api.clientsListWithLastUpload().then(
      () => {},
      () => {},
    );
    // Fall back to /v1/clients with a substring filter that uniquely
    // matches the row by client_id when accountants click into a row.
    api
      .clientsList()
      .then((r) => {
        if (cancelled) return;
        const me = r.clients.find((c) => c.client_id === clientId);
        if (me) {
          // The base list endpoint doesn't return paused state today.
          // We default to false; the toggle is single-source-of-truth via
          // the round-trip on click.
          setPaused(false);
        }
      })
      .catch(() => {
        if (!cancelled) setPaused(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const flip = async (next: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await api.clientSetRemindersPaused(clientId, next);
      setPaused(next);
    } catch {
      setError("שינוי המצב נכשל.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="md">
      <Group justify="space-between">
        <Stack gap={2}>
          <Text fw={500}>השהיית תזכורות</Text>
          <Text size="sm" c="dimmed">
            כשמופעל, מתזמן התזכורות מדלג על לקוח זה בכל הקמפיינים הפעילים.
            שימושי לתקופות חופשה / לקוח שביקש להפסיק את התזכורות.
          </Text>
          {error && <Text c="red" size="sm">{error}</Text>}
        </Stack>
        <Switch
          checked={paused === true}
          onChange={(e) => flip(e.currentTarget.checked)}
          disabled={busy || paused === null}
          label={paused ? "מושהה" : "פעיל"}
        />
      </Group>
    </Card>
  );
}
